import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Pause, RefreshCw } from "lucide-react";

// PAUSA-NEW: pausa de las tareas automáticas de esta plataforma, previa a un intercambio de la copia completa.
// Solo aparece si el backend tiene encendido el restaurador (si no, GET /backup/pausa-tareas/estado responde 404).
// Muestra conteos, ids de instancia y códigos: nunca valores de documentos.

function detalle(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return "No se pudo completar el pedido.";
  return typeof d === "string" ? d : JSON.stringify(d);
}

export default function PausaTareas() {
  const [info, setInfo] = useState(null);
  const [apagado, setApagado] = useState(false);
  const [frase, setFrase] = useState("");
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/backup/pausa-tareas/estado");
      setInfo(data);
      setApagado(false);
    } catch (e) {
      if (e?.response?.status === 404) setApagado(true);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const activa = !!info?.marcador?.activa;
  useEffect(() => {
    if (!activa) return undefined;
    const t = setInterval(cargar, 5000);
    return () => clearInterval(t);
  }, [activa, cargar]);

  if (apagado || !info) return null;

  const enviar = async (ruta, ok) => {
    setEnviando(true);
    try {
      await api.post(`/backup/pausa-tareas/${ruta}`, { confirmacion: frase });
      toast.success(ok);
      setFrase("");
      await cargar();
    } catch (e) {
      toast.error(detalle(e));
    } finally {
      setEnviando(false);
    }
  };

  const m = info.marcador;
  const v = info.veredicto || {};
  const frasePausar = `PAUSAR TAREAS ${info.vivas}`;
  return (
    <Card className="p-6 space-y-4" data-testid="pausa-tareas">
      <div className="flex items-start gap-3">
        <Pause className="h-5 w-5 text-slate-600 mt-0.5" />
        <div>
          <h3 className="font-semibold text-slate-900">Pausar tareas automáticas (antes de intercambiar)</h3>
          <p className="text-sm text-slate-500">
            Detiene en cada instancia el correo, Bókun, cupos, vencimientos, recordatorios y difusiones. Sigue el respaldo de la noche.
            El intercambio solo corre si TODAS las instancias de esta lista confirmaron la pausa y no tienen nada en curso.
            No pausa las rutas web ni los webhooks, ni el arranque del servidor.
          </p>
        </div>
      </div>

      <div className="text-xs text-slate-600 space-y-1" data-testid="pausa-estado">
        <div>
          Pausa: <b>{activa ? `ACTIVA (generación ${m.generacion})` : "no activa"}</b>
          {m?.levantada_en && !activa ? ` · levantada ${m.levantada_en}` : ""}
          {" · "}Instancias vivas: {info.vivas} · Esta instancia: {info.yo}
        </div>
        <table className="w-full text-left" data-testid="pausa-instancias">
          <thead>
            <tr><th>Instancia</th><th>Latido</th><th>Esperada</th><th>Acuse</th><th>En curso</th></tr>
          </thead>
          <tbody>
            {(info.instancias || []).map((i) => (
              <tr key={i.instancia} className={i.fresca ? "" : "text-slate-400"}>
                <td>{i.instancia}</td>
                <td>{i.visto_hace_s == null ? "—" : `hace ${i.visto_hace_s}s`}{i.fresca ? "" : " (no vigente)"}</td>
                <td>{i.esperada == null ? "—" : i.esperada ? "sí" : "no"}</td>
                <td>{i.generacion_acusada == null ? "—" : `gen ${i.generacion_acusada}${i.acuse_hace_s != null ? ` · hace ${i.acuse_hace_s}s` : ""}`}</td>
                <td>{i.ocupada ? JSON.stringify(i.en_curso) : "nada"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {activa ? (
          v.listo ? (
            <div className="text-emerald-700" data-testid="pausa-lista">Lista: todas las instancias esperadas confirmaron y no tienen nada en curso.</div>
          ) : (
            <pre className="whitespace-pre-wrap rounded bg-amber-50 p-2 text-amber-900" data-testid="pausa-motivos">{JSON.stringify(v.motivos, null, 2)}</pre>
          )
        ) : null}
      </div>

      <div className="space-y-2">
        <label className="text-xs text-slate-500">
          Para pausar escribí <b>{frasePausar}</b> (la cantidad tiene que ser la de instancias vivas que ves arriba y que coincide con las que corren). Para levantarla escribí <b>REANUDAR TAREAS</b>.
        </label>
        <div className="flex flex-wrap gap-2">
          <Input className="max-w-[20rem]" placeholder="frase de confirmación" value={frase} onChange={(e) => setFrase(e.target.value)} data-testid="pausa-frase" />
          <Button disabled={activa || enviando || info.vivas < 1 || frase !== frasePausar} data-testid="pausa-activar-btn"
            onClick={() => enviar("activar", "Pausa activada: falta el acuse de cada instancia.")}>
            Pausar tareas
          </Button>
          <Button variant="outline" disabled={!activa || enviando || frase !== "REANUDAR TAREAS"} data-testid="pausa-reanudar-btn"
            onClick={() => enviar("reanudar", "Pausa levantada.")}>
            Reanudar tareas
          </Button>
          <Button variant="outline" disabled={enviando} onClick={cargar} data-testid="pausa-actualizar-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Actualizar
          </Button>
        </div>
      </div>
    </Card>
  );
}
